import { localize } from "@gtmhub/localization";
import {
  GridFilterDefinitions,
  GridFilterEntry,
  IGridFilter,
  IGridFilterDefinition,
  OrFiltersGroup,
  toGridDefinitionMap,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { getCurrentDate, substractMonthsFromNowInUTC } from "../../../../e2e-playwright/src/lib/utils/date-helper";

export const columnsFieldLabelMap = {
  dateTime: "grid_col_dateTime",
  user: "grid_col_user",
  role: "grid_col_role",
  action: "grid_col_action",
  type: "grid_col_type",
  item: "grid_col_item",
  description: "grid_col_description",
};

export const defaultColWidth = {
  large: 180,
  medium: 175,
  small: 155,
  smallest: 50,
};

export const minColumnWidth = {
  large: 180,
  medium: 150,
  small: 100,
};

export const maxColumnWidth = {
  large: 450,
  small: 200,
};

export const typeEntries: GridFilterEntry[] = [
  {
    displayName: localize("automation"),
    value: "automation",
  },
  {
    displayName: localize("objective"),
    value: "goal",
  },
  {
    displayName: localize("key_result"),
    value: "metric",
  },
  {
    displayName: localize("team_cap"),
    value: "team",
  },
  {
    displayName: localize("user_cap"),
    value: "user",
  },
  {
    displayName: localize("task"),
    value: "task",
  },
  {
    displayName: localize("kpi"),
    value: "kpi",
  },
  {
    displayName: localize("kpi_group"),
    value: "kpi_group",
  },
  {
    displayName: localize("badge"),
    value: "badge",
  },
  {
    displayName: localize("custom_field"),
    value: "custom_field",
  },
  {
    displayName: localize("insightboard"),
    value: "dashboard",
  },
  {
    displayName: localize("api_token"),
    value: "api_token_info",
  },
  {
    displayName: localize("session"),
    value: "session",
  },
  {
    displayName: localize("datasource_cap"),
    value: "datasource",
  },
  {
    displayName: localize("metric_link_cap"),
    value: "metric_link",
  },
  {
    displayName: localize("apps"),
    value: "plugin",
  },
  {
    displayName: localize("single_sign_on"),
    value: "ad_connection",
  },
  {
    displayName: localize("insightboard_parameter"),
    value: "dashboard_parameter",
  },
  {
    displayName: localize("insightboard_tag"),
    value: "dashboard_tag",
  },
  {
    displayName: localize("account_name_and_domain"),
    value: "account",
  },
  {
    displayName: localize("insight"),
    value: "insight",
  },
  {
    displayName: localize("comment"),
    value: "comment",
  },
  {
    displayName: localize("task_attachment"),
    value: "attachment",
  },
  {
    displayName: localize("datasource_connection"),
    value: "connection",
  },
  {
    displayName: localize("okr_examples"),
    value: "goal_templates",
  },
  {
    displayName: localize("insight_templates"),
    value: "insight_templates",
  },
  {
    displayName: localize("insight_board_templates"),
    value: "insight_board_templates",
  },
  {
    displayName: localize("slack_integration"),
    value: "integration",
  },
  {
    displayName: localize("list"),
    value: "list",
  },
  {
    displayName: localize("account_notification_settings"),
    value: "notification_settings",
  },
  {
    displayName: localize("objective_link"),
    value: "objective_link",
  },
  {
    displayName: localize("user_role_cap"),
    value: "role",
  },
  {
    displayName: localize("tag_management_cap"),
    value: "tags",
  },
  {
    displayName: localize("team_manager"),
    value: "team_manager",
  },
  {
    displayName: localize("whiteboard_cap"),
    value: "whiteboard",
  },
  {
    displayName: localize("workday_settings"),
    value: "workday_settings",
  },
];

export const actionEntries: GridFilterEntry[] = [
  {
    displayName: localize("created_cap"),
    value: "created",
  },
  {
    displayName: localize("deleted_cap"),
    value: "deleted",
  },
  {
    displayName: localize("updated_cap"),
    value: "modified",
  },
  {
    displayName: localize("installed_cap"),
    value: "installed",
  },
  {
    displayName: localize("uninstalled_cap"),
    value: "uninstalled",
  },
  {
    displayName: localize("requested_cap"),
    value: "requested",
  },
  {
    displayName: localize("rejected_cap"),
    value: "rejected",
  },
  {
    displayName: localize("deactivated_cap"),
    value: "deactivated",
  },
  {
    displayName: localize("assigned_cap"),
    value: "assigned",
  },
  {
    displayName: localize("unassigned_cap"),
    value: "unassigned",
  },
  {
    displayName: localize("comment"),
    value: "comment",
  },
  {
    displayName: localize("progress_changed_cap"),
    value: "progress changed",
  },
  {
    displayName: localize("approved"),
    value: "approved",
  },
];

export const defaultCustomFieldGHGridColumns = (): IGHGridColumn[] => [
  {
    fieldName: "dateTime",
    width: 250,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "date",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "item",
    width: defaultColWidth.large,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "item",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "type",
    width: defaultColWidth.small,
    minWidth: minColumnWidth.small,
    maxWidth: maxColumnWidth.small,
    type: "alphabetic",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "action",
    width: defaultColWidth.large,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "alphabetic",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "user",
    width: defaultColWidth.medium,
    minWidth: minColumnWidth.medium,
    maxWidth: maxColumnWidth.small,
    type: "user",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "description",
    width: 350,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "alphabetic",
    isCustom: false,
    visible: true,
  },
  {
    fieldName: "role",
    width: defaultColWidth.large,
    minWidth: minColumnWidth.large,
    maxWidth: maxColumnWidth.large,
    type: "alphabetic",
    isCustom: false,
    visible: false,
  },
];

export const auditLogFilterDefinitions = (auditLogOptions, auditTrailMonthsLimit: number): GridFilterDefinitions => {
  const baseDefinitions: IGridFilterDefinition[] = [];

  baseDefinitions.push({
    fieldName: "dateTime",
    displayName: localize("date"),
    trackingName: "Date and Time",
    dataType: "date",
    minDate: getMinDate(auditTrailMonthsLimit),
    maxDate: getCurrentDate(),
    arrayType: "static",
    local: true,
    placeholder: localize("search_criteria"),
    defaultOperator: "isBetween",
    supportedOperators: ["isBetween"],
  });

  baseDefinitions.push({
    fieldName: "user",
    displayName: localize("grid_col_user"),
    trackingName: "User",
    dataType: "array",
    arrayType: "assignee",
    placeholder: localize("search_for_user"),
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
  });

  baseDefinitions.push({
    fieldName: "action",
    displayName: localize("grid_col_action"),
    trackingName: "Action",
    dataType: "array",
    arrayType: "static",
    placeholder: localize("search_for_action"),
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
    entries: mergeFilterEntries(actionEntries, auditLogOptions.kinds),
  });

  baseDefinitions.push({
    fieldName: "type",
    displayName: localize("grid_col_type"),
    trackingName: "Type",
    dataType: "array",
    arrayType: "static",
    placeholder: localize("search_for_type"),
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
    entries: mergeFilterEntries(typeEntries, auditLogOptions.targets),
  });

  return toGridDefinitionMap([...baseDefinitions]);
};

const mergeFilterEntries = (defaultEntries: GridFilterEntry[], activeEntries: string[]): GridFilterEntry[] => {
  return defaultEntries.filter((entry) => {
    const defaultEntry = typeof entry !== "string" ? (entry.value as string) : entry;
    return activeEntries.includes(defaultEntry.toLowerCase());
  });
};

const getMinDate = (auditTrailMonthsLimit: number) => {
  return substractMonthsFromNowInUTC(auditTrailMonthsLimit).split("T")[0];
};

export const generateDefaultCustomFieldsFilters = (): OrFiltersGroup[] => {
  const filtersGroup: OrFiltersGroup = [];

  const dateAndTimeFilter: IGridFilter = {
    fieldName: "dateTime",
    dataType: "date",
    operator: "isBetween",
    value: {
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
    },
  };
  filtersGroup.push(dateAndTimeFilter);

  const typeFilter: IGridFilter = {
    fieldName: "type",
    dataType: "array",
    operator: "isOneOf",
    value: [],
  };
  filtersGroup.push(typeFilter);

  const actionFilter: IGridFilter = {
    fieldName: "action",
    dataType: "array",
    operator: "isOneOf",
    value: [],
  };
  filtersGroup.push(actionFilter);

  const userFilter: IGridFilter = {
    fieldName: "user",
    dataType: "array",
    operator: "isOneOf",
    value: [],
  };
  filtersGroup.push(userFilter);

  return [filtersGroup];
};

export const buildDisplayType = (target) => {
  const targetToDisplayTypeLocaleKey = {
    kpi: "kpi",
    kpi_group: "kpi_group",
    metric: "key_result",
    metric_link: "metric_link",
    plugin: "apps",
    "plugin version": "apps",
    ad_connection: "single_sign_on",
    dashboard: "insightboard",
    dashboard_parameter: "insightboard_parameter",
    dashboard_tag: "insightboard_tag",
    account: "account_name_and_domain",
    api_token_info: "api_token",
    attachment: "task_attachment",
    automation: "automation",
    badge: "badge",
    comment: "comment",
    connection: "datasource_connection",
    custom_field: "custom_field",
    datasource: "datasource_cap",
    goal: "objective_cap",
    goal_templates: "goal_templates",
    insight: "insight",
    insight_board_templates: "insight_board_templates",
    insight_templates: "insight_templates",
    integration: "slack_integration",
    list: "list",
    notification_settings: "notification_settings",
    objective_link: "objective_link",
    role: "user_role_cap",
    secondary_user: "user_cap",
    session: "session_cap",
    tags: "tag_management_cap",
    task: "task",
    team: "team",
    team_manager: "team_manager",
    user: "user_cap",
    user_role: "role",
    whiteboard: "whiteboard_cap",
    workday_settings: "workday_settings",
  };

  return localize(targetToDisplayTypeLocaleKey[target]);
};

export const buildActionText = (kind) => {
  const updateKinds = ["object created", "object modified", "object deleted", "modified", "status_changed", "renamed", "changed"];
  if (updateKinds.includes(kind)) {
    return localize("updated_cap");
  }

  if (kind === "progress changed") {
    return localize("progress_changed_cap");
  }

  if (kind === "added") {
    return localize("created_cap");
  }

  return localize(`${kind}_cap`);
};

export const mergeParamTypesAndActions = (params) => {
  if (params.type?.includes("user")) {
    params.type = `${params.type},secondary_user`;
  }

  if (params.type?.includes("plugin")) {
    params.type = `${params.type},plugin version`;
  }

  if (params.type?.includes("role")) {
    params.type = `${params.type},user_role`;
  }

  if (params.type?.includes("team_manager")) {
    params.type = `${params.type},team_member`;
  }

  if (params.action?.includes("updated")) {
    params.action = `${params.action},status changed,changed,renamed`;

    if (params.type?.includes("whiteboard")) {
      params.action = `${params.action},object created,object modified,object deleted`;
    }
  }

  if (params.type?.includes("created")) {
    params.action = `${params.action},added`;
  }
};
